import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  updateGroupMCPServerPolicy,
  updateOrganizationMCPServerPolicy,
} from '@kirin/api-client/api/mcp';
import { queryKeys } from '@/hooks/api/queryKeys';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-errors';
import { trackEvent } from '@/lib/analytics';

function pendingKey(securityGroupId: string | null, name: string): string {
  return `${securityGroupId ?? ''}\u0000${name}`;
}

export interface McpEnforcementActions {
  /**
   * Store the decision for the scope (org-wide, or the security group);
   * resolves, never rejects (failures toast).
   */
  setBlocked: (name: string, blocked: boolean) => Promise<void>;
  /**
   * True for this server from the click until the table has refetched, so a
   * second click cannot act on the stale row, in the scope it was made in.
   * Stable across renders within a scope: a click re-renders the cells, not
   * the column definitions.
   */
  isPending: (name: string) => boolean;
}

/**
 * Allow/Block one Inventory MCP server for the organization (KIR-4482), or for
 * one security group when `securityGroupId` is set (KIR-4701) — the
 * endpoint-server counterpart of the gateway's `useGatewayControlToggle`:
 * blocking is confirmed by the caller first; this hook performs the write,
 * reports a failure with a toast, and refreshes the MCP table either way.
 */
export function useMcpEnforcementToggle(
  organizationId: string,
  securityGroupId: string | null = null
): McpEnforcementActions {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const inFlight = useRef(new Set<string>());
  // Bumped on every change to `inFlight` so the table re-renders its cells.
  const [, setPendingVersion] = useState(0);

  const run = useCallback(
    async (
      name: string,
      change: 'block' | 'allow',
      write: () => Promise<unknown>
    ): Promise<void> => {
      // Per scope: one hook serves the organization and every group the tab
      // switches between, and a write still running in one must not mark the
      // row pending in another.
      const key = pendingKey(securityGroupId, name);
      if (inFlight.current.has(key)) return;
      inFlight.current.add(key);
      setPendingVersion(v => v + 1);
      try {
        await write();
        trackEvent('mcp_server_enforcement_change', {
          change,
          scope: securityGroupId ? 'group' : 'organization',
        });
      } catch (error) {
        toast({
          title: 'Failed to update MCP server policy',
          description: getApiErrorMessage(error, 'Please try again.'),
          variant: 'destructive',
        });
      } finally {
        // Refetch on failure too: another admin may have changed the row.
        await queryClient
          .invalidateQueries({ queryKey: queryKeys.mcp.all })
          .catch(() => undefined);
        inFlight.current.delete(key);
        setPendingVersion(v => v + 1);
      }
    },
    [queryClient, securityGroupId, toast]
  );

  const setBlocked = useCallback(
    (name: string, blocked: boolean): Promise<void> =>
      run(name, blocked ? 'block' : 'allow', () =>
        securityGroupId
          ? updateGroupMCPServerPolicy(
              apiClient,
              organizationId,
              securityGroupId,
              name,
              { blocked }
            )
          : updateOrganizationMCPServerPolicy(apiClient, organizationId, name, {
              blocked,
            })
      ),
    [organizationId, run, securityGroupId]
  );

  const isPending = useCallback(
    (name: string) => inFlight.current.has(pendingKey(securityGroupId, name)),
    [securityGroupId]
  );

  return { setBlocked, isPending };
}
